import { computed } from 'vue'
import { storeToRefs } from 'pinia'

import { useAuthorizationStore } from '@/stores/authorization'
import { useProjectStore } from '@/stores/project'

export const useProjectUserPermissions = () => {
  const authorizationStore = useAuthorizationStore()
  const { currentRoles } = storeToRefs(authorizationStore)
  const { currentItem: currentProject } = storeToRefs(useProjectStore())

  const appRole = computed(() => currentRoles.value.find((role) => role.auth_source === 'User'))
  const isAppAdmin = computed(() => appRole.value?.name === 'App admin')

  const hasGlobalPermission = (permission: string) =>
    isAppAdmin.value &&
    authorizationStore.hasServicePermission({
      authSource: 'User',
      allowed: [permission]
    })

  const hasProjectPermission = (permission: string) =>
    authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: [permission]
    })

  const permissionsReady = computed(
    () => Boolean(currentProject.value?.id) && currentRoles.value.length > 0
  )
  const canViewProjectUsers = computed(
    () =>
      hasGlobalPermission('find_all_projectuser') ||
      hasGlobalPermission('view_projectuser') ||
      hasProjectPermission('view_projectuser')
  )
  const canAddProjectUsers = computed(
    () =>
      !currentProject.value?.is_demo &&
      (hasGlobalPermission('add_projectuser') || hasProjectPermission('add_projectuser'))
  )
  const canChangeProjectUsers = computed(
    () =>
      !currentProject.value?.is_demo &&
      (hasGlobalPermission('change_projectuser') || hasProjectPermission('change_projectuser'))
  )
  const canDeleteProjectUsers = computed(
    () =>
      !currentProject.value?.is_demo &&
      (hasGlobalPermission('delete_projectuser') || hasProjectPermission('delete_projectuser'))
  )
  const canEditProjectUsers = computed(
    () => canChangeProjectUsers.value || canDeleteProjectUsers.value
  )

  return {
    permissionsReady,
    canViewProjectUsers,
    canAddProjectUsers,
    canChangeProjectUsers,
    canDeleteProjectUsers,
    canEditProjectUsers
  }
}
